import { useGetContext } from '@api/lendingAPI/lapiHooks';
import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import { useQuery } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';

import AccountCard from './accountCard/AccountCard';
import styles from './AccountHistory.module.css';
import { useStateKey } from '../../iwQueryClient/useStateKey';
import PartyCalculatorSVG from '../../svgComponents/partyCalculatorSVG/PartyCalculatorSVG';
import { useTrackPageView } from '../../tracking/pageView';
import MainContentPending from '../pageStructure/mainContentPending/';

const AccountHistory = () => {
    useTrackPageView({ name: 'Account History' });

    const stateKey = useStateKey();
    const {
        isLoading,
        isError,
        data: getAccountsResponse
    } = useQuery(getAccountsQueryConfig(stateKey));

    const { data } = useGetContext();

    const isAccessUser = data?.logged_in_user_type === 'access_user';

    if (isLoading || isError) {
        return <MainContentPending data-testid="account-history-loading" />;
    }

    const accountsWithLoans = getAccountsResponse.data!.accounts.filter(
        (account) => account.account_id
    );

    if (!accountsWithLoans.length) {
        return (
            <div className={styles.AccountHistoryNone}>
                {isAccessUser ? (
                    <h1 className={styles.title}>
                        <FormattedMessage
                            id="_.account_history.accessUserNoAccounts"
                            defaultMessage="You currently don't have any personal guarantees"
                        />
                    </h1>
                ) : (
                    <>
                        <h1 className={styles.title}>
                            <FormattedMessage
                                id="_.account_history.no_accounts"
                                defaultMessage="You currently don't have any transaction history"
                            />
                        </h1>

                        <p className={styles.subTitle}>
                            <FormattedMessage
                                id="_.account_history.subTitle"
                                defaultMessage="This is where you can see your repayments, withdrawals and any other transactions."
                            />
                        </p>
                    </>
                )}

                <hr className={styles.divider} />

                <PartyCalculatorSVG />
            </div>
        );
    }

    return (
        <div className={styles.AccountHistory}>
            {isAccessUser ? (
                <h1 className={styles.title}>
                    <FormattedMessage
                        id="_.account_history.accessUserTitle"
                        defaultMessage="Personal Guarantees"
                    />
                </h1>
            ) : (
                <>
                    <h1 className={styles.title}>
                        <FormattedMessage
                            id="_.account_history.title"
                            defaultMessage="Your transaction history"
                        />
                    </h1>

                    <p className={styles.subTitle}>
                        <FormattedMessage
                            id="_.account_history.subTitle"
                            defaultMessage="This is where you can see your repayments, withdrawals and any other transactions."
                        />
                    </p>
                </>
            )}

            <hr className={styles.divider} />

            <div className={styles.accountCards}>
                {accountsWithLoans.map((account) => (
                    <AccountCard
                        key={account.account_id}
                        accountId={account.account_id!}
                        productType={account.product_type!}
                        balance={account.balance!.total!}
                        duration={account.duration!}
                    />
                ))}
            </div>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default AccountHistory;
